import { useNavigate } from 'react-router-dom';
import {
  Waypoints,
  FileText,
  CheckCircle2,
  Scale,
  Ban,
  Flag,
  StickyNote,
  Target,
  Server,
  ListChecks,
  Trash2,
  type LucideIcon,
} from 'lucide-react';
import { Badge } from '../../ui';
import { formatDate, kindLabel, kindTone } from '../../features/timeline/timelineModel';
import type {
  DerivedTimelineEvent,
  TimelineEventKind,
} from '../../features/timeline/types';

const KIND_ICONS: Record<TimelineEventKind, LucideIcon> = {
  workstream_started: Waypoints,
  document_created: FileText,
  document_approved: CheckCircle2,
  decision_made: Scale,
  blocker_raised: Ban,
  blocker_resolved: CheckCircle2,
  milestone: Flag,
  custom: StickyNote,
};

export interface TimelineViewProps {
  events: DerivedTimelineEvent[];
  goalTitles: Map<string, string>;
  workstreamTitles: Map<string, string>;
  serviceTitles: Map<string, string>;
  storyTitles: Map<string, string>;
  /** Delete handler for explicit events (source.type === 'event'). */
  onDeleteEvent?: (id: string) => void;
}

/** Navigates a derived event to its source entity's page. */
function sourceHref(ev: DerivedTimelineEvent): string | undefined {
  const { type, id } = ev.source;
  if (!id) return undefined;
  switch (type) {
    case 'workstream':
      return `/workstreams/${encodeURIComponent(id)}`;
    case 'document':
      return `/documents/${encodeURIComponent(id)}`;
    case 'decision':
      return `/decisions/${encodeURIComponent(id)}`;
    case 'goal':
      return `/goals/${encodeURIComponent(id)}`;
    case 'blocker':
      return '/blockers';
    default:
      return undefined;
  }
}

/**
 * The Engineering Timeline — a vertical, newest-first stream of derived + explicit
 * events, each with a kind badge, date, detail, cross-entity link chips, and a
 * click-through to its source.
 */
export function TimelineView({
  events,
  goalTitles,
  workstreamTitles,
  serviceTitles,
  storyTitles,
  onDeleteEvent,
}: TimelineViewProps) {
  const navigate = useNavigate();

  return (
    <ol className="relative space-y-4 border-l border-surface-border pl-6">
      {events.map((ev) => {
        const Icon = KIND_ICONS[ev.kind] ?? StickyNote;
        const href = sourceHref(ev);
        return (
          <li key={ev.id} className="relative">
            <span className="absolute -left-[31px] flex h-6 w-6 items-center justify-center rounded-full border border-surface-border bg-surface-2 text-accent">
              <Icon className="h-3.5 w-3.5" aria-hidden="true" />
            </span>
            <div className="rounded-2xl border border-surface-border bg-surface-2 px-4 py-3 shadow-card">
              <div className="flex items-start justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={kindTone(ev.kind)}>{kindLabel(ev.kind)}</Badge>
                  <span className="text-xs text-zinc-500">{formatDate(ev.date)}</span>
                </div>
                {onDeleteEvent && ev.source.type === 'event' && ev.source.id && (
                  <button
                    type="button"
                    onClick={() => onDeleteEvent(ev.source.id!)}
                    aria-label="Delete event"
                    className="rounded-md p-1 text-zinc-500 transition-colors hover:bg-surface-3 hover:text-red-300"
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                )}
              </div>

              <button
                type="button"
                onClick={() => href && navigate(href)}
                disabled={!href}
                className="mt-1.5 block text-left"
              >
                <span className={`text-sm font-medium text-zinc-100 ${href ? 'hover:text-white' : ''}`}>
                  {ev.title}
                </span>
              </button>
              {ev.detail && (
                <p className="mt-0.5 line-clamp-2 text-xs text-zinc-400">{ev.detail}</p>
              )}

              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                {ev.links.engineer && (
                  <span className="text-xs text-zinc-500">{ev.links.engineer}</span>
                )}
                {ev.links.goalId && (
                  <button type="button" onClick={() => navigate(`/goals/${encodeURIComponent(ev.links.goalId!)}`)}>
                    <Badge tone="accent"><Target className="h-3 w-3" /> {goalTitles.get(ev.links.goalId) ?? 'Goal'}</Badge>
                  </button>
                )}
                {ev.links.workstreamId && (
                  <button type="button" onClick={() => navigate(`/workstreams/${encodeURIComponent(ev.links.workstreamId!)}`)}>
                    <Badge tone="info"><Waypoints className="h-3 w-3" /> {workstreamTitles.get(ev.links.workstreamId) ?? 'Workstream'}</Badge>
                  </button>
                )}
                {(ev.links.serviceRefs ?? []).map((s) => (
                  <button key={s} type="button" onClick={() => navigate(`/services/${encodeURIComponent(s)}`)}>
                    <Badge tone="neutral"><Server className="h-3 w-3" /> {serviceTitles.get(s) ?? s}</Badge>
                  </button>
                ))}
                {ev.links.storyId && (
                  <Badge tone="neutral"><ListChecks className="h-3 w-3" /> {storyTitles.get(ev.links.storyId) ?? 'Story'}</Badge>
                )}
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export default TimelineView;
